import { Link } from "react-router-dom";
import { CATEGORY_LABELS } from "../lib/constants";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function Kicker({ children, className = "" }) {
    return (
        <span className={`eyebrow inline-flex items-center gap-1.5 text-accent ${className}`}>
            <span className="w-1 h-1 rounded-full bg-accent" />
            {children}
        </span>
    );
}

export function ArticleCard({ article, variant = "default" }) {
    if (!article) return null;
    const label = CATEGORY_LABELS[article.category] || article.category;

    if (variant === "hero") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`hero-article-${article.id}`}
                className="group block animate-fade-up"
            >
                <div className="relative overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
                    <div className="aspect-[16/10] sm:aspect-video overflow-hidden bg-surface-raised">
                        {article.thumbnail && (
                            <img
                                src={article.thumbnail}
                                alt={article.title}
                                className="w-full h-full object-cover opacity-90 transition-[transform,opacity] duration-700 group-hover:scale-[1.04] group-hover:opacity-100"
                            />
                        )}
                    </div>
                    {/* Legibility scrim + inset copy */}
                    <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/70 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
                        <Kicker>{label}</Kicker>
                        <h2 className="font-display text-2xl sm:text-4xl lg:text-[2.75rem] font-bold leading-[1.08] tracking-[-0.03em] mt-3 text-foreground group-hover:text-accent transition-colors duration-200 line-clamp-3">
                            {article.title}
                        </h2>
                        {article.excerpt && (
                            <p className="hidden sm:block mt-4 text-[15px] text-dim max-w-2xl leading-relaxed line-clamp-2">
                                {article.excerpt}
                            </p>
                        )}
                        <div className="mt-4 flex items-center gap-2 font-mono text-[11px] text-dim">
                            {article.author && <span className="truncate max-w-[220px]">{article.author}</span>}
                            {article.author && <span className="w-1 h-1 rounded-full bg-line-strong" />}
                            <span>{timeAgo(article.published_at)}</span>
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    if (variant === "compact") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`article-card-${article.id}`}
                className="group flex gap-4 p-3 -mx-3 rounded-xl hover:bg-surface transition-colors duration-150"
            >
                <div className="flex-1 min-w-0">
                    <Kicker className="!text-[10px]">{label}</Kicker>
                    <h3 className="font-display text-[15px] font-semibold leading-snug tracking-[-0.01em] mt-1.5 text-foreground group-hover:text-accent transition-colors duration-150 line-clamp-3">
                        {article.title}
                    </h3>
                    <span className="font-mono text-[11px] text-dim mt-1.5 block">{timeAgo(article.published_at)}</span>
                </div>
                {article.thumbnail && (
                    <div className="w-[88px] h-[72px] shrink-0 overflow-hidden rounded-lg border border-line bg-surface-raised">
                        <img
                            src={article.thumbnail}
                            alt={article.title}
                            className="w-full h-full object-cover opacity-85 transition-opacity duration-200 group-hover:opacity-100"
                        />
                    </div>
                )}
            </Link>
        );
    }

    return (
        <Link
            to={`/article/${article.id}`}
            data-testid={`article-card-${article.id}`}
            className="group block animate-fade-up rounded-2xl border border-line bg-surface shadow-card overflow-hidden transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-line-strong hover:shadow-lift"
        >
            <div className="aspect-[3/2] overflow-hidden bg-surface-raised">
                {article.thumbnail ? (
                    <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover opacity-90 transition-[transform,opacity] duration-500 group-hover:scale-[1.05] group-hover:opacity-100"
                    />
                ) : (
                    <div className="w-full h-full bg-surface-raised" />
                )}
            </div>
            <div className="p-5">
                <Kicker className="!text-[10px]">{label}</Kicker>
                <h3 className="font-display text-lg font-semibold leading-snug tracking-[-0.015em] mt-2.5 text-foreground group-hover:text-accent transition-colors duration-150">
                    {article.title}
                </h3>
                {article.excerpt && (
                    <p className="mt-2.5 text-sm text-dim leading-relaxed line-clamp-3">{article.excerpt}</p>
                )}
                <div className="mt-4 pt-4 border-t border-line flex items-center gap-2 font-mono text-[11px] text-dim">
                    {article.author && <span className="truncate max-w-[160px]">{article.author}</span>}
                    {article.author && <span className="w-1 h-1 rounded-full bg-line-strong" />}
                    <span>{timeAgo(article.published_at)}</span>
                </div>
            </div>
        </Link>
    );
}

export { timeAgo };
